import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, comicCategories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'popularity' | 'rating' | 'latest'>('popularity');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeSlug]);

  const categoryName = useMemo(() => {
    const cat = comicCategories.find(c => c.slug === activeSlug);
    return cat ? cat.name : '全部';
  }, [activeSlug]);

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all' ? [...comics] : comics.filter(c => c.category === activeSlug);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
        result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
      default:
        result.sort((a, b) => b.popularity - a.popularity);
    }
    return result;
  }, [activeSlug, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const catTitle = activeSlug === 'all' ? '全部漫画' : `${categoryName}漫画`;

  useEffect(() => {
    document.title = `${catTitle} - 八仔漫画网在线免费阅读`;
  }, [catTitle]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                {catTitle}
              </h1>
              <span className="text-sm text-foreground-500">
                共{filteredComics.length}部
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap gap-2">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                {[
                  { key: 'popularity' as const, label: '人气' },
                  { key: 'rating' as const, label: '评分' },
                  { key: 'latest' as const, label: '最新' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-white text-primary-600'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 pb-10">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    rating={comic.rating}
                    status={comic.status}
                    latestChapter={comic.latestChapter}
                    tags={comic.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                <p className="mt-3 text-foreground-500">该分类暂无漫画作品</p>
                <Link to="/category/all" className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600">
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}